import { createPaginatedApiQuery } from "hooks/createPaginatedQuery";
import { externalManufacturingApi } from "./api";
import { createApiQuery } from "hooks/createApiQuery";
import { assertIsDefined } from "utilities/assertIsDefined";
import { useMutation } from "hooks/useMutation";
import { parsePatchData } from "utilities/parsePatchData";
import { OrderLineItemDetails } from "./models";
import { PartialOf } from "typeUtilities";
import { UUID } from "api/types";
import { externalManufacturingKeys } from "./keys";
import { getAnyErrorKey } from "utilities";
import { useSelector } from "hooks";
import { FLAVOR } from "CONSTANTS";

const useExternalManufacturingOrders = createPaginatedApiQuery(externalManufacturingApi.getOrders);
const useExternalManufacturingOrder = createApiQuery(
  externalManufacturingApi.getManufacturingOrder,
);

const useManufacturerItems = createPaginatedApiQuery(externalManufacturingApi.getManufacturerItems);
const useOrderLineItems = createPaginatedApiQuery(externalManufacturingApi.getOrderLineItems);
const useManufacturerItem = createApiQuery(externalManufacturingApi.getManufacturerItem);
const useOrderLineItem = createApiQuery(externalManufacturingApi.getOrderLineItem);

const useManufacturerLineItems = createPaginatedApiQuery(
  externalManufacturingApi.getManufacturerLineItems,
);

const useManufacturerLineItem = createApiQuery(externalManufacturingApi.getManufacturerLineItem);

const usePostConfirmDeliveryDate = () => {
  const me = useSelector(state => state.auth.user);
  const getDetailsKey = (id: UUID) => {
    if (FLAVOR === "externalManufacturing") {
      return externalManufacturingKeys.externalManufacturer.manufacturingLineItems.details(
        String(id),
      );
    }
    return externalManufacturingKeys.main.orderLineItems.details(String(id));
  };
  const listKey =
    FLAVOR === "externalManufacturing"
      ? externalManufacturingKeys.externalManufacturer.manufacturingLineItems.list()
      : externalManufacturingKeys.main.orderLineItems.list();

  return useMutation(externalManufacturingApi.postConfirmDeliveryDate, ({ queryUtils }) => ({
    onMutate: id => {
      const prevPanel = queryUtils.handleMutate(getDetailsKey(id), {
        hasConfirmedDeliveryDate: true,
        deliveryDateConfirmedAt: new Date(),
        deliveryDateConfirmedBy: me,
      });
      const prevList = queryUtils.handlePaginatedListUpdate(listKey, id, {
        hasConfirmedDeliveryDate: true,
        deliveryDateConfirmedAt: new Date(),
        deliveryDateConfirmedBy: me,
      });
      return { prevList, prevPanel };
    },
    onError: (error, id, onMutateReturn) => {
      assertIsDefined(onMutateReturn);
      queryUtils.rollback(getDetailsKey(id), onMutateReturn.prevPanel, error);
      queryUtils.rollbackList(listKey, onMutateReturn.prevList, id);
    },
  }));
};

const usePatchOrderLineItem = () => {
  return useMutation(
    ({ id, toUpdate }: { id: UUID | number; toUpdate: PartialOf<OrderLineItemDetails> }) => {
      return externalManufacturingApi.patchOrderLineItem(parsePatchData(toUpdate), id);
    },
    ({ queryUtils }) => ({
      onMutate: ({ id, toUpdate }) => {
        const prevPanel = queryUtils.handleMutate(
          externalManufacturingKeys.main.orderLineItems.details(String(id)),
          toUpdate,
        );
        const prevList = queryUtils.handlePaginatedListUpdate(
          externalManufacturingKeys.main.orderLineItems.list(),
          id,
          toUpdate,
        );
        return { prevList, prevPanel };
      },
      onError: (error, { id }, onMutateReturn) => {
        assertIsDefined(onMutateReturn);
        queryUtils.rollback(
          externalManufacturingKeys.main.orderLineItems.details(String(id)),
          onMutateReturn.prevPanel,
          error,
        );
        queryUtils.rollbackList(
          externalManufacturingKeys.main.orderLineItems.list(),
          onMutateReturn.prevList,
          id,
        );
      },
    }),
  );
};
const usePatchBulkUpdateLineItems = () => {
  return useMutation(
    externalManufacturingApi.bulkUpdateLineItems,
    ({ queryClient, queryUtils, toastr }) => ({
      onSuccess: () => {
        queryClient.invalidateQueries();
      },
      onError: error => {
        toastr.open({
          text: getAnyErrorKey(error),
          title: "Wymagane działanie",
          type: "warning",
        });
      },
    }),
  );
};

const usePatchManufacturerLineItem = () => {
  return useMutation(
    ({ id, toUpdate }: { id: UUID | number; toUpdate: PartialOf<OrderLineItemDetails> }) => {
      return externalManufacturingApi.patchManufacturerLineItem(parsePatchData(toUpdate), id);
    },
    ({ queryUtils }) => ({
      onMutate: ({ id, toUpdate }) => {
        const prevPanel = queryUtils.handleMutate(
          externalManufacturingKeys.externalManufacturer.manufacturingLineItems.details(String(id)),
          toUpdate,
        );
        const prevList = queryUtils.handlePaginatedListUpdate(
          externalManufacturingKeys.externalManufacturer.manufacturingLineItems.list(),
          id,
          toUpdate,
        );
        return { prevList, prevPanel };
      },
      onError: (error, { id }, onMutateReturn) => {
        assertIsDefined(onMutateReturn);
        queryUtils.rollback(
          externalManufacturingKeys.externalManufacturer.manufacturingLineItems.details(String(id)),
          onMutateReturn.prevPanel,
          error,
        );
        queryUtils.rollbackList(
          externalManufacturingKeys.externalManufacturer.manufacturingLineItems.list(),
          onMutateReturn.prevList,
          id,
        );
      },
    }),
  );
};

export const useSendEmailNotification = () => {
  return useMutation(
    externalManufacturingApi.postNotificationToRecipient,
    ({ queryClient, toastr }) => ({
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(
          externalManufacturingKeys.main.manufacturingOrders.details(variables.manufacturingOrder),
        );
        toastr.open({
          type: "success",
          title: "Udało się!",
          text: "Pomyślnie wysłano powiadomienie email",
        });
      },
      onError: error => {
        toastr.open({
          type: "warning",
          title: "Wymagane działanie",
          text: getAnyErrorKey(error),
        });
      },
    }),
  );
};

export const externalManufacturingActions = {
  useExternalManufacturingOrders,
  useExternalManufacturingOrder,
  useManufacturerItems,
  useManufacturerItem,
  useManufacturerLineItems,
  useManufacturerLineItem,
  useOrderLineItem,
  useOrderLineItems,
  usePatchBulkUpdateLineItems,
  usePatchManufacturerLineItem,
  useSendEmailNotification,
  usePostConfirmDeliveryDate,
  usePatchOrderLineItem,
};
